import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useToast } from '../context/ToastContext';

interface Staff {
  id: number;
  name: string;
}

export interface SchoolClass {
  id?: number;
  name: string;
  year?: number;
  segment?: string;
  shift?: string;
  responsible?: number;
  responsible_name?: string;
  is_active: boolean;
}

interface SchoolClassFormProps {
  onSuccess: () => void;
  onCancel: () => void;
  classToEdit?: SchoolClass | null;
}

const SchoolClassForm: React.FC<SchoolClassFormProps> = ({ onSuccess, onCancel, classToEdit }) => {
  const { addToast } = useToast();
  const [name, setName] = useState('');
  const [responsible, setResponsible] = useState<number | ''>('');
  const [isActive, setIsActive] = useState(true);
  const [allStaff, setAllStaff] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const response = await api.get('staff/?active_only=true');
        setAllStaff(response.data);
      } catch (error) {
        console.error('Failed to fetch staff', error);
      }
    };
    fetchStaff();
  }, []);

  useEffect(() => {
    if (classToEdit) {
      setName(classToEdit.name);
      setResponsible(classToEdit.responsible || '');
      setIsActive(classToEdit.is_active);
    } else {
      setName('');
      setResponsible('');
      setIsActive(true);
    }
  }, [classToEdit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const classData = { name, responsible: responsible || null, is_active: isActive };

    try {
      if (classToEdit && classToEdit.id) {
        await api.patch(`classes/${classToEdit.id}/`, classData);
        addToast('Turma atualizada com sucesso!', 'success');
      } else {
        await api.post('classes/', classData);
        addToast('Nova turma cadastrada!', 'success');
      }
      onSuccess();
    } catch (error) {
      console.error('Failed to save class', error);
      addToast('Erro ao salvar turma.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const SectionTitle = ({ title, icon }: { title: string, icon: string }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px', marginTop: '10px' }}>
      <span style={{ fontSize: '1.2rem' }}>{icon}</span>
      <h4 style={{ margin: 0, fontSize: '0.95rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--primary-blue)' }}>{title}</h4>
    </div>
  );

  return (
    <div className="card" style={{ maxWidth: '600px', margin: '0 auto' }}>
      <div style={{ marginBottom: '25px', borderBottom: '1px solid var(--border-color)', paddingBottom: '15px' }}>
        <h3 style={{ margin: 0 }}>{classToEdit ? '🏫 Editar Turma' : '✨ Nova Turma Escolar'}</h3>
      </div>

      <form onSubmit={handleSubmit}>
        <SectionTitle title="Identificação" icon="📝" />
        <div className="form-group">
          <label>Nome da Turma / Ano</label>
          <input 
            type="text" 
            value={name} 
            onChange={(e) => setName(e.target.value)} 
            placeholder="Ex: 5º Ano A - Matutino"
            required 
          />
        </div>

        <div style={{ marginTop: '30px' }}>
          <SectionTitle title="Responsabilidade" icon="👤" />
        </div>

        <div className="form-group">
          <label>Professor / Colaborador Responsável</label>
          <select value={responsible} onChange={(e) => setResponsible(e.target.value === '' ? '' : parseInt(e.target.value))}>
            <option value="">Nenhum responsável selecionado...</option>
            {allStaff.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          <small style={{ color: 'var(--text-light)', marginTop: '4px', display: 'block' }}>
            O colaborador que acompanhará as métricas desta turma.
          </small>
        </div>

        <div style={{ 
          marginTop: '40px', 
          paddingTop: '20px', 
          borderTop: '1px solid var(--border-color)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
            <input 
              type="checkbox" 
              checked={isActive} 
              onChange={(e) => setIsActive(e.target.checked)}
              style={{ width: '20px', height: '20px' }}
            />
            <span style={{ fontWeight: '600', color: isActive ? 'var(--success)' : 'var(--danger)' }}>
              {isActive ? 'Turma Ativa' : 'Turma Inativa'}
            </span>
          </label>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={onCancel} className="btn btn-secondary">Cancelar</button>
            <button type="submit" className="btn" disabled={loading}>
              {loading ? 'Salvando...' : 'Salvar Turma'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SchoolClassForm;
